/**
 * Session 8 Block 1 · BrandingScan page.
 *
 * User journey:
 *   1. Enter website URL, click "Analyse starten"
 *   2. Poll GET /branding/scan/{id} every 6s while status ∈
 *      {queued, crawling, analyzing}
 *   3. Show substep progress derived from status
 *   4. When completed: render editable suggestion cards
 *   5. Apply selected fields → POST /apply → brand_profile updated
 *
 * Tests must NOT hit the real network — the api client is mocked.
 */
import React, { useState, useEffect, useRef } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Alert, AlertDescription, AlertTitle }
  from "../../components/ui/alert";
import {
  Globe, Sparkles, Palette, Users, Building2, CheckCircle2,
  AlertTriangle, Loader2, RefreshCcw,
} from "lucide-react";


const STATUS_SUBSTEP = {
  queued: "Warten auf Start…",
  crawling: "Website wird gecrawlt…",
  analyzing: "Zielgruppen + USPs werden erkannt…",
  completed: "Fertig",
  failed: "Fehler",
  timeout: "Timeout",
};


function ProgressCard({ status, sourceUrl }) {
  return (
    <Card data-testid="branding-progress-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Loader2 className="h-4 w-4 animate-spin" />
          {STATUS_SUBSTEP[status] || "Läuft…"}
        </CardTitle>
        <CardDescription>{sourceUrl}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2 text-sm text-muted-foreground">
        <div className="flex items-center gap-2">
          <Globe className="h-3 w-3" />
          Robots.txt geprüft, Homepage + Unterseiten werden geladen…
        </div>
        <div className="flex items-center gap-2">
          <Palette className="h-3 w-3" />
          Farbschema wird aus CSS extrahiert…
        </div>
        <div className="flex items-center gap-2">
          <Sparkles className="h-3 w-3" />
          KI analysiert Ton, Zielgruppen, USPs…
        </div>
      </CardContent>
    </Card>
  );
}


function SuggestionsView({ scan, onApply, onRescan }) {
  const [selectAll, setSelectAll] = useState(true);
  const [overwrite, setOverwrite] = useState(false);
  const [saving, setSaving] = useState(false);
  const s = scan.suggestions || {};
  const logo = s.logo;
  const colors = s.colors || {};

  const apply = async (fields) => {
    setSaving(true);
    try {
      const r = await api.post(
        `/branding/scan/${scan.scan_id}/apply`,
        { fields: fields || ["all"], overwrite });
      toast.success(
        `Übernommen: ${(r.data.applied_fields || []).length} Felder`);
      onApply && onApply(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail?.error || "apply_failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="branding-suggestions">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">
          Vorschläge für dein Markenprofil
        </h2>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={onRescan}
                    data-testid="btn-branding-rescan">
            <RefreshCcw className="h-3 w-3 mr-1" />
            Neu analysieren
          </Button>
          <Button onClick={() => apply(["all"])} disabled={saving}
                   data-testid="btn-branding-apply-all">
            {saving ? "Übernehmen…" : "Alle übernehmen"}
          </Button>
        </div>
      </div>

      {logo && (
        <Card data-testid="branding-card-logo">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              Logo
              <Badge variant="secondary">
                Konfidenz {Math.round((logo.confidence || 0) * 100)}%
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="flex items-center gap-4">
            <img src={logo.source_url} alt="Logo"
                  className="h-16 w-auto max-w-[120px] object-contain
                              rounded border p-1 bg-white"
                  data-testid="branding-logo-preview" />
            <div className="text-xs text-muted-foreground break-all">
              <div>Quelle: {logo.detected_via}</div>
              <div>{logo.source_url}</div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card data-testid="branding-card-colors">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Palette className="h-4 w-4" />
            Farbschema
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 flex-wrap">
            {(colors.palette || []).map((hex) => (
              <div key={hex}
                    className="rounded border p-2 text-xs
                              flex items-center gap-2">
                <div className="h-6 w-6 rounded border"
                      style={{ backgroundColor: hex }} />
                <code>{hex}</code>
              </div>
            ))}
            {(!colors.palette || !colors.palette.length) && (
              <p className="text-xs text-muted-foreground">
                Keine Farben extrahiert.
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {s.brand_voice && (
        <Card data-testid="branding-card-voice">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Ton der Marke</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="mb-2">
              <Badge>{s.brand_voice.tone || "professional"}</Badge>
            </div>
            <ul className="text-xs text-muted-foreground list-disc pl-4">
              {(s.brand_voice.sample_phrases || []).map((p, i) => (
                <li key={i}>{p}</li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {(s.target_audiences || []).length > 0 && (
        <Card data-testid="branding-card-audiences">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Users className="h-4 w-4" />
              Zielgruppen
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {s.target_audiences.map((a, i) => (
              <div key={i} className="rounded border p-2">
                <div className="font-medium text-sm">{a.name}</div>
                <div className="text-xs text-muted-foreground">
                  {a.description}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {(s.regions || []).length > 0 && (
        <Card data-testid="branding-card-regions">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Regionen
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-1">
            {s.regions.map((r) => (
              <Badge key={r} variant="secondary">{r}</Badge>
            ))}
          </CardContent>
        </Card>
      )}

      {(s.usps || []).length > 0 && (
        <Card data-testid="branding-card-usps">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">USPs</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="text-sm list-disc pl-4">
              {s.usps.map((u, i) => <li key={i}>{u}</li>)}
            </ul>
          </CardContent>
        </Card>
      )}

      {s.industry_guess && (
        <Card data-testid="branding-card-industry">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Branche</CardTitle>
          </CardHeader>
          <CardContent>
            <Badge>{s.industry_guess}</Badge>
          </CardContent>
        </Card>
      )}

      <div className="flex items-center gap-2 pt-2">
        <input type="checkbox" id="overwrite-flag"
                checked={overwrite}
                onChange={(e) => setOverwrite(e.target.checked)}
                data-testid="branding-overwrite-flag" />
        <Label htmlFor="overwrite-flag" className="text-xs">
          Bestehende Profil-Felder überschreiben
        </Label>
      </div>
    </div>
  );
}


export default function BrandingScan() {
  const [url, setUrl] = useState("");
  const [scanId, setScanId] = useState(null);
  const [scan, setScan] = useState(null);
  const [error, setError] = useState(null);
  const [starting, setStarting] = useState(false);
  const pollRef = useRef(null);

  const stopPoll = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  useEffect(() => { return stopPoll; }, []);

  const startScan = async () => {
    setError(null); setStarting(true); setScan(null);
    try {
      const r = await api.post("/branding/scan", { url });
      setScanId(r.data.scan_id);
      setScan({ scan_id: r.data.scan_id, status: "queued",
                  source_url: url });
      const pollOnce = async () => {
        try {
          const p = await api.get(`/branding/scan/${r.data.scan_id}`);
          setScan(p.data);
          if (["completed", "failed", "timeout"].includes(p.data.status)) {
            stopPoll();
          }
        } catch (e) { /* transient poll error */ }
      };
      // Immediate first poll so the UI transitions past 'queued' fast,
      // then interval polling as backup.
      await pollOnce();
      pollRef.current = setInterval(pollOnce, 6000);
    } catch (e) {
      setError(e.response?.data?.detail || { error: "scan_start_failed" });
    } finally { setStarting(false); }
  };

  const reset = () => {
    stopPoll(); setScanId(null); setScan(null); setError(null);
  };

  if (error) {
    const reasonMap = {
      robots_disallow:
        "Die Website erlaubt keinen automatischen Zugriff " +
        "(robots.txt). Bitte manuell weiter.",
      invalid_url: "Die URL ist ungültig. Bitte prüfen.",
      internal_url_blocked:
        "Diese Adresse zeigt auf ein internes Netz und wird nicht "
        + "gescannt (Sicherheit).",
      brand_scan_rate_limit:
        "Maximum 3 Scans pro Stunde erreicht. Bitte später wieder.",
      // Session 8 Block 5 · New reasons.
      llm_provider_not_configured:
        "Um die KI zu nutzen, richte zuerst deinen KI-Provider ein.",
      plan_limit_exceeded_brand_scans:
        "Monatliches Analyse-Limit erreicht. "
        + "Upgrade deinen Plan für mehr Scans.",
      plan_feature_locked:
        "Diese Analyse ist in deinem Plan nicht enthalten. Upgrade nötig.",
      subscription_missing:
        "Kein aktives Abo. Bitte kontaktiere den Plattform-Betreiber.",
    };
    const cta = error.cta;
    return (
      <div className="max-w-2xl mx-auto p-6 space-y-4"
            data-testid="branding-scan-page">
        <Alert data-testid="branding-error-alert">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Analyse nicht möglich</AlertTitle>
          <AlertDescription>
            {reasonMap[error.error] || reasonMap[error.reason_code]
              || error.hint || "Unbekannter Fehler."}
          </AlertDescription>
        </Alert>
        <div className="flex gap-2">
          {cta && cta.route && (
            <Button onClick={() => { window.location.href = cta.route; }}
                     data-testid="btn-branding-cta">
              {cta.label || "Weiter"}
            </Button>
          )}
          <Button variant="outline" onClick={reset}
                   data-testid="btn-branding-retry">
            Erneut versuchen
          </Button>
        </div>
      </div>
    );
  }

  if (scan && ["queued", "crawling", "analyzing"].includes(scan.status)) {
    return (
      <div className="max-w-2xl mx-auto p-6"
            data-testid="branding-scan-page">
        <ProgressCard status={scan.status} sourceUrl={scan.source_url} />
      </div>
    );
  }

  if (scan && scan.status === "completed") {
    return (
      <div className="max-w-3xl mx-auto p-6 space-y-4"
            data-testid="branding-scan-page">
        <Alert className="border-emerald-300 bg-emerald-50/40"
                data-testid="branding-success-alert">
          <CheckCircle2 className="h-4 w-4" />
          <AlertTitle>Analyse abgeschlossen</AlertTitle>
          <AlertDescription>
            {scan.pages_crawled || 0} Seiten in {" "}
            {Math.round((scan.duration_ms || 0) / 100) / 10}s untersucht.
          </AlertDescription>
        </Alert>
        <SuggestionsView scan={scan}
                         onApply={() => toast.success("Markenprofil aktualisiert.")}
                         onRescan={reset} />
      </div>
    );
  }

  if (scan && (scan.status === "failed" || scan.status === "timeout")) {
    return (
      <div className="max-w-2xl mx-auto p-6 space-y-4"
            data-testid="branding-scan-page">
        <Alert data-testid="branding-error-alert">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Analyse fehlgeschlagen</AlertTitle>
          <AlertDescription>
            Grund: <code>{scan.error || scan.status}</code>. Bitte
            das Markenprofil manuell ausfüllen.
          </AlertDescription>
        </Alert>
        <Button onClick={reset} data-testid="btn-branding-retry">
          Erneut versuchen
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-6 space-y-4"
          data-testid="branding-scan-page">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5" />
            Markenprofil automatisch erstellen
          </CardTitle>
          <CardDescription>
            Gib deine Firmenwebsite ein. Wir crawlen bis zu 30 Unterseiten
            und schlagen dir Logo, Farben, Ton, Zielgruppen, Regionen und
            USPs vor — du kannst alles bearbeiten oder ablehnen.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="branding-url-input">Website</Label>
            <Input id="branding-url-input"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="https://firma.de"
                    data-testid="branding-url-input" />
            <p className="text-xs text-muted-foreground mt-1">
              Öffentlich erreichbare URL. Wir respektieren robots.txt
              und crawlen ausschließlich deine Domain.
            </p>
          </div>
          <Button onClick={startScan} disabled={!url || starting}
                    data-testid="btn-branding-start">
            {starting ? "Startet…" : "Analyse starten"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
